import React, { memo, useEffect, useState, useRef, useMemo } from 'react';
import { View } from '@tarojs/components';
import GameCard from '@/components/GameCard';
import * as Taro from '@tarojs/taro';
import { BOARD_HEIGHT, BOARD_WIDTH, fitBoardToWindow } from '@/data/board';
import { Card } from '@/types/game';
import { paintedCards } from '@/utils/boardVisibility';
import styles from './index.module.scss';

interface GameBoardProps {
  cards: Card[];
  onCardClick: (cardId: string) => void;
  hiddenCardId?: string;
  hiddenCardIds?: string[];
  onLayout?: () => void;
  effect?: string;
}

const GameBoard: React.FC<GameBoardProps> = ({ cards, onCardClick, hiddenCardId, hiddenCardIds = [], onLayout, effect = '' }) => {
  const [, onResize] = useState(0);
  useEffect(() => { const resize = () => onResize(n => n + 1); window.addEventListener('resize', resize); return () => window.removeEventListener('resize', resize); }, []);
  const previousCards = useRef(cards);
  if (effect !== 'shuffle-gather') previousCards.current = cards;
  const visibleCards = effect === 'shuffle-gather' ? previousCards.current : cards;
  const paint = useMemo(() => paintedCards(visibleCards, hiddenCardId ? [...hiddenCardIds, hiddenCardId] : hiddenCardIds), [visibleCards, hiddenCardId, hiddenCardIds]);
  const windowInfo = typeof Taro.getWindowInfo === 'function' ? Taro.getWindowInfo() : { windowWidth: 375, windowHeight: 650 };
  const [scale, setScale] = useState(() => fitBoardToWindow(windowInfo.windowWidth, windowInfo.windowHeight));
  const boardId = cards[0]?.id;
  useEffect(() => {
    if (typeof Taro.createSelectorQuery !== 'function') return;
    Taro.createSelectorQuery().select('.sheep-board-host').boundingClientRect().exec(results => {
      const bounds = results[0] as { width: number; height: number } | null;
      if (!bounds || bounds.width <= 0 || bounds.height <= 0) return;
      const rpx = windowInfo.windowWidth / 750;
      setScale(Math.min(1, bounds.width / (BOARD_WIDTH * rpx), bounds.height / (BOARD_HEIGHT * rpx)));
    });
  }, [boardId, windowInfo.windowWidth, windowInfo.windowHeight]);
  useEffect(() => {
    const timer = setTimeout(() => onLayout?.(), 50);
    return () => clearTimeout(timer);
  }, [scale, boardId, onLayout]);
  return (
    <View className={`${styles.board} sheep-board-host`} data-board-effect={effect}>
      <View id='board-canvas' className={styles.canvas} style={{ width: `${BOARD_WIDTH}rpx`, height: `${BOARD_HEIGHT}rpx`, transform: `translate(-50%, -50%) scale(${scale})` }}>
      {paint.map((card, index) => {
        if (card.status !== 0) {
          return null;
        }

        return (
          <GameCard
            key={card.id}
            card={card}
            onClick={onCardClick}
            x={card.x}
            y={card.y}
            zIndex={card.zIndex}
            hidden={card.id === hiddenCardId || hiddenCardIds.includes(card.id)}
            motionDisabled={!!effect}
            artHidden={effect === 'shuffle-gather'}
            motionTransform={effect === 'shuffle-gather' ? 'translate3d(' + (314 - card.x) + 'rpx,' + (300 - card.y) + 'rpx,0) scale(.42) rotate(' + (index % 2 ? 4 : -4) + 'deg)' : undefined}
          />
        );
      })}
      </View>
    </View>
  );
};

export default memo(GameBoard);
